/**
 * Pure helpers used by the MSAL login flow.
 * They do not touch the MSAL instance, so they can be unit tested in isolation.
 */

const SIGNIN_POLICY = /b2c_1_signin/i;
const FORGOT_PASSWORD_CODE = 'AADB2C90118';
const B2C_USER_CANCELLED_CODE = 'AADB2C90091';
const MSAL_WINDOW_NAME_PREFIX = 'msal.';

const LoginErrors = {
    cancelled: 'cancelled',
    inProgress: 'inProgress',
    forgotPassword: 'forgotPassword',
    other: 'other',
};

/**
 * True when the url hash carries an auth response (code, error or state) from Azure B2C.
 */
const isAuthResponseHash = (hash) => /(^#|&)(code|error|state)=/.test(hash || '');

/**
 * True when the page runs inside an MSAL popup or a hidden iframe.
 */
const isPopupOrFrame = (win) => {
    const hasOpener = Boolean(win.opener && win.opener !== win),
        inFrame = win.parent !== win,
        msalNamed = String(win.name || '').indexOf(MSAL_WINDOW_NAME_PREFIX) === 0;
    return hasOpener || inFrame || msalNamed;
};

/**
 * True when the page is the landing page of an MSAL popup/iframe.
 * The opener reads the auth response from the url, so the app must not boot there:
 * the router would replace the url and the response would be lost.
 */
const isMsalResponseWindow = (win) => isAuthResponseHash(win.location.hash) && isPopupOrFrame(win);

/**
 * Picks the account to use for silent token requests.
 * Order: active account, the account used at the last login, an account issued by the sign-in policy, any account.
 */
const selectAccount = (active, accounts, preferredHomeAccountId) => {
    if (active) {
        return active;
    }
    const list = accounts || [],
        preferred = preferredHomeAccountId && list.find(a => a.homeAccountId === preferredHomeAccountId);
    return preferred || list.find(a => SIGNIN_POLICY.test(a.homeAccountId || '')) || list[0] || null;
};

/**
 * Maps an MSAL/B2C error to one of LoginErrors.
 */
const classifyLoginError = (err, forgotPasswordCode = FORGOT_PASSWORD_CODE) => {
    const error = err || {},
        code = error.errorCode || '',
        message = `${error.errorMessage || ''} ${error.message || ''}`;

    if (message.indexOf(forgotPasswordCode || FORGOT_PASSWORD_CODE) >= 0) {
        return LoginErrors.forgotPassword;
    }
    if (code === 'user_cancelled' || message.indexOf(B2C_USER_CANCELLED_CODE) >= 0) {
        return LoginErrors.cancelled;
    }
    if (code === 'interaction_in_progress') {
        return LoginErrors.inProgress;
    }
    return LoginErrors.other;
};

/**
 * Short display name from the account claims, e.g. "John D".
 * Falls back to name / username when given_name or family_name are missing.
 */
const displayName = (account) => {
    const acc = account || {},
        claims = acc.idTokenClaims || {},
        first = claims.given_name || '',
        lastInitial = (claims.family_name || '').charAt(0);

    if (first) {
        return lastInitial ? `${first} ${lastInitial}` : first;
    }
    return claims.name || acc.name || acc.username || '';
};

export {
    LoginErrors,
    classifyLoginError,
    displayName,
    isAuthResponseHash,
    isMsalResponseWindow,
    isPopupOrFrame,
    selectAccount,
};
